/**
 * Notes HORS PLAN — les remarques listées sous (ou à côté) du fond de plan :
 * textes sans leader posés hors de l'emprise de la vue, souvent les uns sous
 * les autres, parfois avec un marqueur photo en bout de ligne. Chaque LIGNE
 * est une remarque (commentaire global, ou désordre général quand le texte a
 * été écrit avec une note de légende active) : un groupe à part entière, que
 * la passe photos peut compléter.
 *
 * Les textes d'une même ligne (continuation horizontale) sont réunis ; les
 * lignes successives restent séparées — c'est l'IA qui, en LISANT le texte,
 * fusionne une continuation ou classe commentaire / désordre.
 */

import { bboxCenter } from "./geometry";
import {
  NOTE_ROW_GAP_FACTOR,
  NOTE_ROW_OVERLAP_RATIO,
} from "./grouping-constants";
import type { AnalysisEntity, WorldBbox } from "./types";

/** Un texte sans leader dont le centre est hors de l'emprise de la vue. */
export function isOffPlanText(
  entity: AnalysisEntity,
  contentBbox: WorldBbox,
): boolean {
  if (!entity.bbox || !entity.text?.trim()) return false;
  if (entity.leaderTips?.length) return false;
  const c = bboxCenter(entity.bbox);
  return (
    c.x < contentBbox.minX ||
    c.x > contentBbox.maxX ||
    c.y < contentBbox.minY ||
    c.y > contentBbox.maxY
  );
}

/** Deux textes sur la même ligne : recouvrement vertical et écart horizontal courts. */
function sameRow(a: WorldBbox, b: WorldBbox): boolean {
  const ha = a.maxY - a.minY;
  const hb = b.maxY - b.minY;
  const overlap = Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY);
  if (overlap < NOTE_ROW_OVERLAP_RATIO * Math.min(ha, hb)) return false;
  const gap = Math.max(0, a.minX - b.maxX, b.minX - a.maxX);
  return gap <= NOTE_ROW_GAP_FACTOR * Math.max(ha, hb);
}

/**
 * Réunit les textes hors plan par LIGNE. Lignes rendues de haut en bas, les
 * textes d'une ligne de gauche à droite (ordre de lecture).
 */
export function groupOffPlanRows(texts: AnalysisEntity[]): AnalysisEntity[][] {
  const items = texts.filter((t) => t.bbox !== null);
  const parent = items.map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (sameRow(items[i].bbox as WorldBbox, items[j].bbox as WorldBbox)) {
        parent[find(j)] = find(i);
      }
    }
  }
  const rows = new Map<number, AnalysisEntity[]>();
  for (let i = 0; i < items.length; i++) {
    const root = find(i);
    const row = rows.get(root);
    if (row) row.push(items[i]);
    else rows.set(root, [items[i]]);
  }
  const top = (row: AnalysisEntity[]): number =>
    Math.max(...row.map((t) => (t.bbox as WorldBbox).maxY));
  const left = (row: AnalysisEntity[]): number =>
    Math.min(...row.map((t) => (t.bbox as WorldBbox).minX));
  return [...rows.values()]
    .map((row) =>
      row.sort(
        (a, b) => (a.bbox as WorldBbox).minX - (b.bbox as WorldBbox).minX,
      ),
    )
    .sort((a, b) => top(b) - top(a) || left(a) - left(b));
}
